import React, { useMemo, useState } from "react";
import {
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

export default function DownloadCreatedReport({ baseUrl, withCredentials = false }) {
  // Prefer your existing env; fall back to optional prop, then current origin
  const API_BASE =
    process.env.REACT_APP_PAYMENT_API ||
    baseUrl ||
    window.location.origin;

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const isRangeInvalid = useMemo(() => {
    if (!from || !to) return false;
    return new Date(from) > new Date(to);
  }, [from, to]);

  function buildUrl() {
    const url = new URL("/reports/DownloadCreated", API_BASE);
    if (from) url.searchParams.set("from", from); // yyyy-mm-dd
    if (to) url.searchParams.set("to", to);
    return url.toString(); // absolute URL
  }

  function extractFilename(disposition) {
    if (!disposition) return null;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disposition);
    try {
      return decodeURIComponent((m && (m[1] || m[2])) || "");
    } catch {
      return (m && (m[1] || m[2])) || null;
    }
  }

  async function handleDownload() {
    setError(null);
    if (isRangeInvalid) {
      setError("The 'From' date cannot be later than the 'To' date.");
      return;
    }
    setDownloading(true);
    try {
      const res = await fetch(buildUrl(), {
        method: "GET",
        credentials: withCredentials ? "include" : "same-origin",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const disposition = res.headers.get("Content-Disposition");
      const filename =
        extractFilename(disposition) ||
        `CreatedReport_${from || "all"}_${to || "all"}.xlsx`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e && e.message) || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="max-w-3xl overflow-hidden rounded-2xl border bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-5 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
          <DocumentChartBarIcon className="h-6 w-6 text-blue-700" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Transactions Report</h2>
          <p className="text-sm text-gray-500">
            Download a formal Excel report for created transactions within the selected date range.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="space-y-5 px-5 py-5">
        {/* Date range */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col text-sm">
            <span className="mb-1 inline-flex items-center gap-1 text-gray-700">
              <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
              From (optional)
            </span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 inline-flex items-center gap-1 text-gray-700">
              <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
              To (optional)
            </span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </label>
        </div>

        {/* Validation / errors */}
        {(isRangeInvalid || error) && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
            <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <div>
              {isRangeInvalid ? "The 'From' date cannot be later than the 'To' date." : error}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading || isRangeInvalid}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
            {downloading ? "Preparing…" : "Download Excel"}
          </button>

          <span className="text-xs text-gray-500">
            If dates are omitted, the report will include all created transactions.
          </span>
        </div>

       
      </div>
    </div>
  );
}
